import React, { useEffect, useRef, useState, useCallback } from "react";
import "./Hero.css";

// Preload critical images
const SLIDES = [
  { src: "./headerthree.webp", alt: "Professional massage therapy session" },
  { src: "./headerone.webp", alt: "Relaxing spa environment" },
  { src: "./headertwo.webp", alt: "Wellness treatment in serene setting" }
];

const SLIDES_LENGTH = SLIDES.length;

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [isHovering, setHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const touchStartX = useRef(null);
  const intervalRef = useRef(null);
  const heroRef = useRef(null);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setIsReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setIsReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Intersection Observer for reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    const currentRef = heroRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  // Preload all images
  useEffect(() => {
    SLIDES.forEach((slide, i) => {
      const img = new Image();
      img.src = slide.src;

      if (i === 0) {
        img.fetchPriority = "high";
        img.loading = "eager";
      }

      img.onload = () => {
        setImagesLoaded((prev) => ({
          ...prev,
          [slide.src]: true
        }));
      };
    });
  }, []);

  // Optimized autoplay
  useEffect(() => {
    if (isHovering || isReducedMotion) {
      clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setIndex((currentIndex) => (currentIndex + 1) % SLIDES_LENGTH);
    }, 5000);

    return () => clearInterval(intervalRef.current);
  }, [isHovering, isReducedMotion]);

  const onTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const onTouchEnd = useCallback(
    (e) => {
      if (touchStartX.current === null) return;

      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const THRESHOLD = 40;

      if (dx > THRESHOLD) {
        setIndex((i) => (i - 1 + SLIDES_LENGTH) % SLIDES_LENGTH);
      } else if (dx < -THRESHOLD) {
        setIndex((i) => (i + 1) % SLIDES_LENGTH);
      }

      touchStartX.current = null;
    },
    []
  );

  const goTo = useCallback((i) => setIndex(i), []);
  const handleMouseEnter = useCallback(() => setHovering(true), []);
  const handleMouseLeave = useCallback(() => setHovering(false), []);

  return (
    <section
      ref={heroRef}
      className="hero"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="Hero carousel"
    >
      <div className="hero__slides">
        {SLIDES.map((slide, i) => (
          <div
            key={slide.src}
            className={`hero__slide ${i === index ? "is-active" : ""} ${
              imagesLoaded[slide.src] ? "is-loaded" : ""
            }`}
            style={{ backgroundImage: `url("${slide.src}")` }}
            aria-hidden={i !== index}
          />
        ))}
        <div className="hero__overlay" aria-hidden="true" />
        <div className="hero__gradient" aria-hidden="true" />
      </div>

      <div className={`hero__content ${isVisible ? "is-visible" : ""}`}>
        <div className="hero__content-inner">
          <h1 className="hero__title">
            <span className="hero__title-line hero__title-line--nowrap">
              Unwind with Therapeutic 
            </span>
            <br />
            <span className="hero__title-line">Massage Services</span>
          </h1>

          <p className="hero__sub">
            Designed to Restore Balance, Relieve Tension,
            <br />
            and Help You Feel Your Best
          </p>

          <a
            href="https://mysite.vagaro.com/apeacefulretreat"
            className="hero__cta"
          >
            MAKE AN APPOINTMENT
          </a>

          <div className="hero__dots">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                className={`hero__dot ${i === index ? "is-active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
                type="button"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}